import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';
import * as S from './styles';
import { ProductSlot, ComparisonData, ProductInfo } from './types';
import {
  fetchProductById,
  createStaticComparison,
  parseProductId,
  extractProductDetails,
} from './comparisonService';
import ComparisonResult from './ComparisonResult';
import ImageV2 from 'shared/components/ImageV2';
import { fetchWidgetData as _fetchWidgetData } from 'shared/store/widgets/services';
import useWidgetLoader from 'shared/layouts/DetailsPage/components/WidgetLoader/useWidgetLoader';
import { addToCartPlatform } from 'shared/store/cart/services';
import { useToastContext, TOASTER_TYPES, ALIGNMENT_TYPES } from 'shared/components/Toast';
import {
  getSimilarData,
  getProductIDFromWidgetStore,
  isWidgetFetching as _isWidgetFetching,
  widgetError,
} from 'shared/store/widgets/selectors';
import { getProductId } from 'shared/store/details/selectors';

interface SimilarProduct {
  id: string;
  title: string;
  subTitle: string;
  price: number;
  discountedPrice: number;
  discount: number;
  imageUrl: string;
}

interface ProductComparisonProps {
  currentProductId: string;
  isMobile?: boolean;
  compareProductIds?: string[];
}

const ProductComparison: React.FC<ProductComparisonProps> = ({
  currentProductId,
  isMobile = false,
  compareProductIds
}) => {
  const { id: routeID } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { transformedWidgetData: apiConfig } = useWidgetLoader({ isMobile });

  const hasDirectCompare = compareProductIds && compareProductIds.length > 0;
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [isDirectCompareLoading, setIsDirectCompareLoading] = useState(false);
  const [productSlots, setProductSlots] = useState<ProductSlot[]>([
    { id: 'current', productId: currentProductId, productData: undefined },
    { id: 'slot1' },
    { id: 'slot2' },
  ]);
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  const [isLoadingSlot, setIsLoadingSlot] = useState<string | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [slotDropdownOpen, setSlotDropdownOpen] = useState<{ [key: string]: boolean }>({});
  const slotRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      setSlotDropdownOpen((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((slotId) => {
          if (
            next[slotId] &&
            slotRefs.current[slotId] &&
            !slotRefs.current[slotId]!.contains(e.target as Node)
          ) {
            next[slotId] = false;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get similar products from Redux store
  const similarData: any = useSelector(getSimilarData);
  const similarProducts: SimilarProduct[] = similarData?.products || [];
  const hasSimilarProducts = !isEmpty(similarProducts);

  // Widget fetch selectors
  const productID = useSelector(getProductId);
  const productIDFromWidgetStore = useSelector(getProductIDFromWidgetStore);
  const isWidgetFetching = useSelector(_isWidgetFetching);
  const isFailed = useSelector(widgetError);

  // Fetch similar products from API if not already available when modal opens
  const fetchWidgetIfNeeded = useCallback(() => {
    if (hasSimilarProducts) return;
    if (isFailed || isWidgetFetching) return;
    if (productID !== routeID) return;
    if (productID === productIDFromWidgetStore) return;

    dispatch(
      _fetchWidgetData({
        productID,
        apiConfig,
      })
    );
  }, [
    hasSimilarProducts,
    isFailed,
    isWidgetFetching,
    productID,
    routeID,
    productIDFromWidgetStore,
    dispatch,
    apiConfig,
  ]);

  useEffect(() => {
    if (showSelectionModal) {
      fetchWidgetIfNeeded();
    }
  }, [showSelectionModal, fetchWidgetIfNeeded]);

  // Filter similar products based on input text
  const getFilteredSimilarProducts = (slotId: string) => {
    const query = (inputValues[slotId] || '').toLowerCase().trim();
    if (!query) return similarProducts;
    return similarProducts.filter(
      (sp) =>
        sp.title.toLowerCase().includes(query) ||
        sp.subTitle.toLowerCase().includes(query) ||
        sp.id.includes(query)
    );
  };

  // Load current product when modal opens
  useEffect(() => {
    const loadCurrentProduct = async () => {
      if (showSelectionModal && !productSlots[0].productData) {
        try {
          const currentProduct = await fetchProductById(currentProductId);
          if (currentProduct) {
            setProductSlots((prev) =>
              prev.map((slot, idx) => (idx === 0 ? { ...slot, productData: currentProduct } : slot))
            );
          }
        } catch (err) {
          console.error('Failed to load current product:', err);
        }
      }
    };
    loadCurrentProduct();
  }, [showSelectionModal, currentProductId]);

  const handleAddProduct = async (slotId: string) => {
    const inputValue = inputValues[slotId] || '';
    if (!inputValue.trim()) return;

    const productId = parseProductId(inputValue);
    if (!productId) {
      setError('Invalid product URL or ID');
      return;
    }

    // Check for duplicate product
    const isDuplicate = productSlots.some(
      (slot) => slot.productId === productId || slot.productData?.id === productId
    );
    if (isDuplicate) {
      setError('This product is already added to the comparison');
      return;
    }

    setIsLoadingSlot(slotId);
    setError(null);

    try {
      const productData = await fetchProductById(productId);
      if (!productData) {
        throw new Error('Failed to fetch product');
      }

      setProductSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId ? { ...slot, productId, productData, error: undefined } : slot
        )
      );
      setInputValues((prev) => ({ ...prev, [slotId]: '' }));
    } catch (err: any) {
      setError(err.message || 'Failed to add product');
      setProductSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId ? { ...slot, error: 'Failed to load product' } : slot
        )
      );
    } finally {
      setIsLoadingSlot(null);
    }
  };

  const handleRemoveProduct = (slotId: string) => {
    setProductSlots((prev) => prev.map((slot) => (slot.id === slotId ? { id: slot.id } : slot)));
  };

  const handleSelectSimilarProduct = async (slotId: string, product: SimilarProduct) => {
    // Check for duplicate
    const isDuplicate = productSlots.some(
      (slot) => slot.productId === product.id || slot.productData?.id === product.id
    );
    if (isDuplicate) {
      setError('This product is already added to the comparison');
      return;
    }

    setIsLoadingSlot(slotId);
    setError(null);

    try {
      const productData = await fetchProductById(product.id);
      if (!productData) {
        throw new Error('Failed to fetch product');
      }

      setProductSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId
            ? { ...slot, productId: product.id, productData, error: undefined }
            : slot
        )
      );
    } catch {
      // Fallback: use the similar product data directly (limited fields)
      const fallbackData: ProductInfo = {
        id: product.id,
        title: product.title,
        subTitle: product.subTitle,
        price: product.price,
        discountedPrice: product.discountedPrice,
        discount: product.discount,
        imageUrl: product.imageUrl,
      };
      setProductSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId
            ? { ...slot, productId: product.id, productData: fallbackData, error: undefined }
            : slot
        )
      );
    } finally {
      setIsLoadingSlot(null);
    }
  };

  const handleCompare = async () => {
    // Get all products that have data
    const productsWithData = productSlots.filter((slot) => slot.productData);

    if (productsWithData.length < 2) {
      setError('Please add at least one more product to compare');
      return;
    }

    setIsComparing(true);
    setError(null);

    try {
      // Load current product if not loaded
      if (!productSlots[0].productData) {
        const currentProduct = await fetchProductById(currentProductId);
        if (currentProduct) {
          setProductSlots((prev) =>
            prev.map((slot, idx) => (idx === 0 ? { ...slot, productData: currentProduct } : slot))
          );
          productsWithData[0] = { ...productsWithData[0], productData: currentProduct };
        }
      }

      const products = productsWithData.map((slot) => slot.productData!) as ProductInfo[];
      const comparison = createStaticComparison(products);

      setComparisonData(comparison);
      setShowSelectionModal(false);
      setShowComparisonModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate comparison');
    } finally {
      setIsComparing(false);
    }
  };

  const productDetails = productSlots
    .filter((slot) => slot.productData)
    .map((slot) => extractProductDetails(slot.productData!));

  // Auto-compare when compareProductIds are provided
  useEffect(() => {
    if (!hasDirectCompare) return;

    const runDirectCompare = async () => {
      setIsDirectCompareLoading(true);
      try {
        const allIds = [currentProductId, ...compareProductIds];
        const products = await Promise.all(allIds.map((id) => fetchProductById(id)));
        const validProducts = products.filter(Boolean) as ProductInfo[];

        if (validProducts.length < 2) return;

        // Update slots with fetched data
        setProductSlots(
          validProducts.map((p, idx) => ({
            id: idx === 0 ? 'current' : `slot${idx}`,
            productId: p.id,
            productData: p,
          }))
        );

        const comparison = createStaticComparison(validProducts);
        setComparisonData(comparison);
        setShowComparisonModal(true);
      } catch (err) {
        console.error('Direct comparison failed:', err);
      } finally {
        setIsDirectCompareLoading(false);
      }
    };

    runDirectCompare();
  }, [currentProductId, hasDirectCompare]);

  const { showToast } = useToastContext();

  const handleAddToBag = useCallback(
    (productId: string) => {
      dispatch(
        addToCartPlatform({
          productId,
          quantity: 1,
          deviceType: isMobile ? 'MSITE' : 'WEBSITE',
        }) as any
      ).then(() => {
        showToast({ type: TOASTER_TYPES.SUCCESS, message: 'Item added to cart', textAlign: ALIGNMENT_TYPES.CENTER });
      });
    },
    [dispatch, isMobile, showToast]
  );

  if (hasDirectCompare) {
    return (
      <>
        {isDirectCompareLoading && (
          <S.ModalOverlay>
            <S.LoadingSpinner>
              <div className="spinner" />
            </S.LoadingSpinner>
          </S.ModalOverlay>
        )}

        {showComparisonModal && comparisonData && (
          <ComparisonResult
            productDetails={productDetails}
            comparisonData={comparisonData}
            isMobile={isMobile}
            onClose={() => {
              setShowComparisonModal(false);
              setComparisonData(null);
            }}
            onBack={() => {
              setShowComparisonModal(false);
            }}
            onAddToBag={handleAddToBag}
          />
        )}
      </>
    );
  }

  return (
    <>
      <S.CompareButtonContainer>
        <S.CompareButton onClick={() => setShowSelectionModal(true)}>
          <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 3H5C3.89543 3 3 3.89543 3 5V9C3 10.1046 3.89543 11 5 11H9C10.1046 11 11 10.1046 11 9V5C11 3.89543 10.1046 3 9 3Z"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19 3H15C13.8954 3 13 3.89543 13 5V9C13 10.1046 13.8954 11 15 11H19C20.1046 11 21 10.1046 21 9V5C21 3.89543 20.1046 3 19 3Z"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 13H5C3.89543 13 3 13.8954 3 15V19C3 20.1046 3.89543 21 5 21H9C10.1046 21 11 20.1046 11 19V15C11 13.8954 10.1046 13 9 13Z"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19 13H15C13.8954 13 13 13.8954 13 15V19C13 20.1046 13.8954 21 15 21H19C20.1046 21 21 20.1046 21 19V15C21 13.8954 20.1046 13 19 13Z"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Compare</span>
        </S.CompareButton>
      </S.CompareButtonContainer>

      {showSelectionModal && (
        <S.ModalOverlay onClick={() => setShowSelectionModal(false)}>
          <S.SelectionModal onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <S.ModalHeader>
              <h2>Compare Products</h2>
              <S.CloseButton onClick={() => setShowSelectionModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </S.CloseButton>
            </S.ModalHeader>

            {error && (
              <S.ErrorMessage>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {error}
              </S.ErrorMessage>
            )}

            {productSlots.map((slot, index) => (
              <S.ProductSlot key={slot.id} isCurrentProduct={index === 0}>
                {slot.productData ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        flexShrink: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        borderRadius: '16px 0 0 16px',
                      }}
                    >
                      <ImageV2
                        src={slot.productData.imageUrl}
                        alt={slot.productData.title}
                        width={80}
                        aspectRatio={1}
                        lazy={true}
                      />
                    </div>
                    <S.ProductInfo>
                      <h3>{slot.productData.title}</h3>
                      <p>{slot.productData.subTitle}</p>
                      <p style={{ fontWeight: 600, color: '#000' }}>
                        ₹{slot.productData.discountedPrice?.toLocaleString()}
                      </p>
                    </S.ProductInfo>
                    {index !== 0 && (
                      <S.CloseButton onClick={() => handleRemoveProduct(slot.id)}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </S.CloseButton>
                    )}
                  </div>
                ) : index === 0 ? (
                  <S.LoadingText>Loading current product...</S.LoadingText>
                ) : (
                  <>
                    {isLoadingSlot === slot.id ? (
                      <S.LoadingSpinner>
                        <div className="spinner" />
                      </S.LoadingSpinner>
                    ) : (
                      <S.ComboInputWrapper
                        ref={(el: HTMLDivElement | null) => {
                          slotRefs.current[slot.id] = el;
                        }}
                      >
                        <S.InputGroup>
                          <S.Input
                            type="text"
                            placeholder={
                              hasSimilarProducts
                                ? 'Search similar or enter ID / URL'
                                : 'Enter Product ID or URL'
                            }
                            value={inputValues[slot.id] || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const value = e?.target?.value || '';
                              setInputValues((prev) => ({ ...prev, [slot.id]: value }));
                              if (hasSimilarProducts) {
                                setSlotDropdownOpen((prev) => ({ ...prev, [slot.id]: true }));
                              }
                            }}
                            onFocus={() => {
                              if (hasSimilarProducts) {
                                setSlotDropdownOpen((prev) => ({ ...prev, [slot.id]: true }));
                              }
                            }}
                            onKeyPress={(e: React.KeyboardEvent) =>
                              e.key === 'Enter' && handleAddProduct(slot.id)
                            }
                          />
                          <S.AddButton
                            onClick={() => handleAddProduct(slot.id)}
                            disabled={!inputValues[slot.id]?.trim()}
                          >
                            Add
                          </S.AddButton>
                        </S.InputGroup>
                        {hasSimilarProducts &&
                          slotDropdownOpen[slot.id] &&
                          (() => {
                            const filtered = getFilteredSimilarProducts(slot.id);
                            if (filtered.length === 0) return null;
                            return (
                              <S.DropdownList>
                                {filtered.map((sp) => {
                                  const isAlreadyAdded = productSlots.some(
                                    (s) => s.productId === sp.id || s.productData?.id === sp.id
                                  );
                                  return (
                                    <S.DropdownItem
                                      key={sp.id}
                                      disabled={isAlreadyAdded}
                                      onClick={() => {
                                        handleSelectSimilarProduct(slot.id, sp);
                                        setSlotDropdownOpen((prev) => ({
                                          ...prev,
                                          [slot.id]: false,
                                        }));
                                        setInputValues((prev) => ({ ...prev, [slot.id]: '' }));
                                      }}
                                    >
                                      <S.DropdownItemImage>
                                        <ImageV2
                                          src={sp.imageUrl}
                                          alt={sp.title}
                                          width={44}
                                          aspectRatio={1}
                                          lazy={true}
                                        />
                                      </S.DropdownItemImage>
                                      <S.DropdownItemInfo>
                                        <span>{sp.title}</span>
                                        <span>{sp.subTitle}</span>
                                      </S.DropdownItemInfo>
                                      <S.DropdownItemPrice>
                                        ₹{sp.discountedPrice?.toLocaleString()}
                                      </S.DropdownItemPrice>
                                    </S.DropdownItem>
                                  );
                                })}
                              </S.DropdownList>
                            );
                          })()}
                      </S.ComboInputWrapper>
                    )}
                  </>
                )}
              </S.ProductSlot>
            ))}

            <S.CompareButtonAction
              onClick={handleCompare}
              disabled={productSlots.filter((s) => s.productData).length < 2 || isComparing}
            >
              {isComparing ? 'Comparing...' : 'Compare Now'}
            </S.CompareButtonAction>
          </S.SelectionModal>
        </S.ModalOverlay>
      )}

      {showComparisonModal && comparisonData && (
        <ComparisonResult
          productDetails={productDetails}
          comparisonData={comparisonData}
          isMobile={isMobile}
          onClose={() => {
            setShowComparisonModal(false);
            setComparisonData(null);
          }}
          onBack={() => {
            setShowComparisonModal(false);
            setShowSelectionModal(true);
          }}
          onAddToBag={handleAddToBag}
        />
      )}
    </>
  );
};

export default ProductComparison;
